import type {GridItemStorage} from '@Components/base-grid/components/grid-item';
import type {GridStorageTypes} from '@Components/base-grid';
import type {GridEmailSignupFormItemStorage} from '@Components/email-signup-form-grid/components/email-signup-form-grid';
import type {EmailSignupForm, SignupForm} from '@FormMakerReact/email-signup-form.types';
import {getPosterPlaceholderImagePreviewURL} from '@Libraries/poster-library';
import type {AjaxResponse} from '@Utils/ajax.util';
import {MODAL_IDS, openModal} from '@Components/modal-container-deprecated';
import {getUserId} from '@Libraries/user.library';
import {openErrorModal} from '@Modals/error-modal';
import {noop} from '@Utils/general.util';
import {GA4EventName} from '@Libraries/ga-events';

interface CreateNewSignupFormResponse {
  idEmailSignupForm: string;
}

export const SIGNUP_GRID_ID = 'signupGrid';
export const deleteEmailSignupFormLoading = 'deleteEmailSignupForm';

export const getSignupFormMenuTriggerId = (idEmailSignupForm: string): string => {
  return `signup-form-menu-${idEmailSignupForm}`;
};

export const duplicateEmailSignupFormLoading = 'duplicateEmailSignupForm';
export const updateEmailSignupFormLoadingNotifications = 'updateEmailSignupFormLoadingNotifications';

export interface SignupFormData {
  id: string;
  name: string;
}

export const findGridItem = (grid: GridStorageTypes, gridItemId: string): GridEmailSignupFormItemStorage => {
  return grid.items.find((item: GridItemStorage) => {
    return item.id === gridItemId;
  }) as GridEmailSignupFormItemStorage;
};

export const isSignupFormDeleteForbidden = (response: AjaxResponse): boolean => {
  return response.code === 'signup-form-in-event';
};

export const showSignupFormDeletionForbiddenDialog = (): void => {
  openModal(MODAL_IDS.ERROR_MODAL_ID, {
    message: window.i18next.t('pmwjs_signup_form_deletion_forbidden'),
    log: false,
  });
};
export const shareEmailSignupFormInTeam = async (isSharedInTeam: boolean, idEmailSignupForm: string): Promise<void> => {
  await window.PMW.writeLocal('formmaker/shareEmailSignupFormInTeam', {
    shareInTeam: isSharedInTeam,
    idEmailSignupForm,
  });
};

export const isSignupFormSharedByTeamMember = (signupForm: SignupForm): boolean => {
  return signupForm.isSharedInTeam && signupForm.idOwner !== getUserId();
};

export const isSignupFormPublished = (signupForm: SignupForm | undefined): boolean => {
  if (!signupForm?.publishStatus) {
    return false;
  }

  const {link, popup, embeddedForm} = signupForm.publishStatus;
  return link || popup || embeddedForm;
};

export const getEmailSignupFormById = async (signupFormId: string): Promise<EmailSignupForm | undefined> => {
  return (await window.PMW.readLocal('formmaker/getEmailSignupFormById', {
    signupFormId,
  })) as EmailSignupForm | undefined;
};

export const getDummyEmailSignupForm = (): EmailSignupForm => {
  return {
    areNotificationsEnabled: false,
    createdOn: 0,
    emailsCollected: 5,
    fieldsMeta: [],
    htmlContent: '',
    idOwner: 0,
    isDeleted: false,
    isPublishedEmbeddedForm: false,
    isPublishedLink: false,
    isPublishedPopup: false,
    isPublishedQRCode: false,
    jsonContent: '',
    lastModifiedOn: 0,
    mailingLists: [],
    organizationName: '',
    popupDelay: 0,
    previewURL: getPosterPlaceholderImagePreviewURL(),
    id: 'dummy',
    name: 'Dummy Signup Form',
    isSharedInTeam: false,
  };
};

export const getUserSignupForms = (): Promise<SignupFormData[]> => {
  return window.PMW.readLocal('formmaker/getEmailSignupFormsDataForUser') as Promise<SignupFormData[]>;
};

export const createAndOpenNewSignupForm = (onCreateSuccess: VoidFunction = noop): void => {
  void createBlankSignupFormAndOpenEditor(onCreateSuccess);
};

export const redirectToSignupFormPublishWizard = (idEmailSignupForm: string): void => {
  window.location.assign(window.PMW.util.site_url(`formmaker/publish/${idEmailSignupForm}`));
};

async function createBlankSignupFormAndOpenEditor(onCreateSuccess: VoidFunction): Promise<void> {
  window.PMW.setLoading(window.i18next.t('pmwjs_creating'));
  try {
    const {idEmailSignupForm} = (await window.PMW.writeLocal('formmaker/createNewEmailSignupForm')) as CreateNewSignupFormResponse;
    onCreateSuccess();
    window.PMW.gtm.trackGA4CustomEvent(GA4EventName.SIGNUPFORM_CREATE);
    window.location.href = window.PMW.util.site_url(`formmaker/load/${idEmailSignupForm}`);
  } catch {
    window.PMW.setLoading(null);
    openErrorModal({
      title: window.i18next.t('pmwjs_something_went_wrong'),
      message: window.i18next.t('pmwjs_could_not_create_email_signup_form'),
    });
  }
}
